import React, { useMemo, useState, useEffect } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import dayjs from 'dayjs';

import { customStyles, SMETheme } from './Select';

const PeriodSelect = ({ 
  recurrenceType,
  yearRangeSettings = {},
  value = "", 
  onChange,
  name,
  language,
  placeholders = [I18n.t('select'), I18n.t('select')],  
  disabled,
}) => {
  const [yearPart, subPart] = value ? value.split('_') : ['', ''];
  const formattedSubPart = subPart ? `_${subPart}` : '';

  const yearOptions = useMemo(() => {
    const currentYear = dayjs().year();

    const startYear =
      yearRangeSettings?.rangeType === 'fixed'
        ? Number(yearRangeSettings.startYear) || currentYear
        : currentYear - (Number(yearRangeSettings?.yearsInPast) || 2); // Default to 2 years

    const endYear =
      yearRangeSettings?.rangeType === 'fixed'
        ? Number(yearRangeSettings.endYear) || currentYear
        : currentYear + (Number(yearRangeSettings?.yearsInFuture) || 2); // Default to 2 years

    // Limit the number of years shown in the dropdown
    const maxYearRange = 100;
    const minStartYear = Math.max(currentYear - maxYearRange, startYear);
    const maxEndYear = Math.min(currentYear + maxYearRange, endYear);

    const years = [];

    for (let i = minStartYear; i <= maxEndYear; i++) {
      years.push({ value: `${i}`, label: `${i}` });
    }
    return years;
  }, []);

  const subPeriodOptions = useMemo(() => {
    const options = [];
    if (recurrenceType === 'monthly') {
      for (let m = 1; m <= 12; m++) {
        const monthNum = m.toString().padStart(2, '0');
        const monthName = new Date(2026, m - 1, 1).toLocaleDateString(language, {month: "long"});
        options.push({ value: `_M${monthNum}`, label: monthName });
      }
    } else if (recurrenceType === 'quarterly') {
      for (let q = 1; q <= 4; q++) {
        options.push({ value: `_Q${q}`, label: `Quarter ${q}` });
      }
    } else if (recurrenceType === 'weekly') {
      for (let w = 1; w <= 52; w++) {
        options.push({ value: `_W${w.toString().padStart(2, '0')}`, label: `Week ${w}` });
      }
    }
    return options;
  }, [recurrenceType, language]);

  const selectedYearOption = useMemo(() => 
    yearPart ? { value: yearPart, label: yearPart } : null
  , [yearPart]);

  const selectedSubOption = useMemo(() => 
    subPeriodOptions.find(o => o.value === formattedSubPart) || null
  , [subPeriodOptions, formattedSubPart]);

  const handleYearChange = (opt) => {
    const newYear = opt ? opt.value : '';
    if (recurrenceType === 'yearly' || !newYear) {
      onChange(newYear);
    } else {
      onChange(`${newYear}${formattedSubPart}`);
    }
  };

  const handleSubChange = (opt) => {
    const newSub = opt ? opt.value : '';
    if (yearPart) {
      onChange(`${yearPart}${newSub}`);
    }
  };

  return (
    <div style={{ display: 'flex', gap: '10px' }}>
      <div style={{ flex: 1 }}>
        <CreatableSelect
          placeholder={placeholders[0]}
          options={yearOptions}
          value={selectedYearOption}
          onChange={handleYearChange}
          style={customStyles}
          theme={SMETheme}
          isDisabled={disabled}
        />
      </div>

      {recurrenceType !== 'yearly' && (
        <div style={{ flex: 1 }}>
          <Select
            placeholder={placeholders[1]}
            options={subPeriodOptions}
            value={selectedSubOption}
            onChange={handleSubChange}
            isDisabled={!yearPart || disabled}
            style={customStyles}
            theme={SMETheme}
          />
        </div>
      )}
    </div>
  );
};

export default PeriodSelect;